import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '~/components/Modal'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { Order, RouteProtocol } from '@tokenlon/v6-sdk'
import RoutingDiagram from './RoutingDiagram'
import { findAllUniqueProtocols, getPercentRoutesEntries } from './utils'
import { LOCALES } from '~/locales'
import {
  StyledDetailTitle,
  StyledDetailDesc,
  StyledRoutesWrapper,
  StyledPercentRouteWrapper,
} from './styled'

interface Props {
  order: Order
}

const SmartRoutingDetailModal: React.FC<Props> = ({ order }) => {
  const { t, i18n } = useTranslation()

  const toggle = useModalToggle(ApplicationModal.SMART_ROUTING_DETAIL)
  const isOpen = useModalOpened(ApplicationModal.SMART_ROUTING_DETAIL)

  const percentRoutesEntries = useMemo(() => getPercentRoutesEntries(order), [
    order,
  ])

  const protocols = useMemo(() => findAllUniqueProtocols(order?.routes), [
    order?.routes,
  ])
  const formatProtocolsStr = (protocols: RouteProtocol[]) => {
    const length = protocols.length
    const isZh = i18n.language === LOCALES.ZH
    const upperProtocols = protocols.map(
      (protocol) => protocol.charAt(0).toUpperCase() + protocol.slice(1),
    )
    if (length > 1) {
      const lastProtocol = upperProtocols[length - 1]
      const restProtocols = upperProtocols.slice(0, length - 1)
      const separator = isZh ? '、' : ', '
      const conjunction = isZh ? '和' : 'and'

      return `${restProtocols.join(separator)} ${conjunction} ${lastProtocol}`
    }
    return upperProtocols.join('')
  }
  const protocolsStr = formatProtocolsStr(protocols)

  const routes = order?.routes
  if (!routes || routes.length === 0) {
    return null
  }

  return (
    <Modal
      animate={false}
      isOpen={isOpen}
      onDismiss={toggle}
      contentClassName="new-modal-base-content smart-routing-details"
    >
      <StyledDetailTitle>{t('smart_routing')}</StyledDetailTitle>
      <StyledDetailDesc>
        {t('smart_routing_detail_modal_desc', { protocols: protocolsStr })}
      </StyledDetailDesc>
      <StyledRoutesWrapper
        direction="column"
        justify="space-between"
        align="center"
        gap={12}
      >
        {percentRoutesEntries.map(({ percentage, percentRoute }, index) => (
          <StyledPercentRouteWrapper key={index}>
            <span>{`${percentage}%`}</span>
            <RoutingDiagram
              fromAddress={order?.fromAddress}
              pathRoutes={percentRoute}
            />
          </StyledPercentRouteWrapper>
        ))}
      </StyledRoutesWrapper>
    </Modal>
  )
}

export default SmartRoutingDetailModal
